import * as React from "react";
import { Typography } from "@mui/material";
import GeneralModal from "../modals/GeneralModal";
import { AuthenticationContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import { useFlash } from "../FlashContext";


interface Props {
  open: boolean;
  reviewId: number | null;
  onClose: () => void;
  onSuccess: (reviewId: number) => void;
}


const InsertPendingReviewModal: React.FC<Props> = ({
  open,
  reviewId,
  onClose,
  onSuccess,
}) => {
  const authCtx = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const [loading, setLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (open) {
      setLoading(false);
    }
  }, [open]);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleInsert = async () => {
    if (reviewId === null) return;

    setLoading(true);
    try {
      const res = await fetch(
        `/data_fetching/manual_eligibility_reviews/${reviewId}/insert_as_patient`,
        { method: "PATCH", headers: getHeaders(authCtx.csrfToken) }
      );
      const result = await res.json();

      if (result.success) {
        showToast({ title: "Success", text: "Patient created successfully.", type: "success" });
        onSuccess(reviewId);
        onClose();
      } else {
        showToast({
          title: "Error",
          text: "Unable to create patient from this pending review. Please try again or contact support.",
          type: "error",
        });
      }
    } catch {
      showToast({
        title: "Error",
        text: "Unable to create patient from this pending review. Please try again or contact support.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <GeneralModal
      open={open}
      title={<span style={{ fontWeight: 600 }}>Create a New Patient?</span>}
      closeCallback={handleClose}
      successCallback={handleInsert}
      confirmButtonText="Create New Patient"
      cancelButtonText="Cancel"
      isLoading={loading}
      width="430px"
    >
      <Typography color="#666666" sx={{ fontSize: "14px" }}>
        This will create a new patient record in Starfield using the eligibility information. Please confirm this record does not already belong to an existing Starfield patient before continuing.
      </Typography>
    </GeneralModal>
  );
};

export default InsertPendingReviewModal;
